import { HandCoins, Receipt, ShieldCheck, User } from 'lucide-react'
import { formatDateTime } from '@/lib/datetime'
import { formatMinorCurrency, humanizeEnum } from '@/lib/format'
import type { CustomerPenalty } from '@/types/api'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { KeyValue, KeyValueGrid } from '@/components/ui/RowActions'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { describeBand, describeWindow } from './describe'
import styles from './PenaltiesPage.module.css'

interface PenaltyDetailsDrawerProps {
  penalty: CustomerPenalty | null
  onClose: () => void
  onWaive: (penalty: CustomerPenalty) => void
}

/**
 * One penalty, in full.
 *
 * This is the screen an operator reads out loud on the phone. "The balance says
 * 250" is not an answer to "why am I being charged?", so everything that
 * produced the number is here: which booking, which band, how long before
 * pickup, and what the booking cost.
 */
export function PenaltyDetailsDrawer({ penalty, onClose, onWaive }: PenaltyDetailsDrawerProps) {
  const open = penalty !== null

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Penalty details"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          {penalty?.waivable ? (
            <Button
              variant="secondary"
              iconLeft={<HandCoins aria-hidden="true" />}
              onClick={() => onWaive(penalty)}
            >
              Waive penalty
            </Button>
          ) : null}
        </>
      }
    >
      {penalty ? (
        <div className={styles.dialogBody}>
          <div className={styles.headline}>
            <StatusBadge status={penalty.status} />
            <span className={styles.headlineAmount}>
              {formatMinorCurrency(penalty.amountMinor, penalty.currency)}
            </span>
          </div>

          <p className={styles.explanation}>{explain(penalty)}</p>

          <Section icon={<Receipt aria-hidden="true" />} title="Why it was charged">
            <KeyValueGrid>
              <KeyValue label="Cancelled booking">
                {penalty.sourceBookingReference ?? `#${penalty.sourceTripRequestId}`}
              </KeyValue>
              <KeyValue label="Booking price">
                {formatMinorCurrency(penalty.bookingPriceMinor, penalty.currency)}
              </KeyValue>
              <KeyValue label="Policy band">{describeBand(penalty)}</KeyValue>
              <KeyValue label="Cancelled">{describeWindow(penalty)}</KeyValue>
              <KeyValue label="Scheduled pickup">{formatDateTime(penalty.scheduledPickupAt)}</KeyValue>
              <KeyValue label="Charged at">{formatDateTime(penalty.createdAt)}</KeyValue>
              <KeyValue label="Reason">{penalty.reason ?? '—'}</KeyValue>
              <KeyValue label="Charged by">
                {penalty.createdByAdminName ?? humanizeEnum(penalty.createdBySource)}
              </KeyValue>
            </KeyValueGrid>
          </Section>

          <Section icon={<User aria-hidden="true" />} title="Customer">
            <KeyValueGrid>
              <KeyValue label="Name">{penalty.customerName ?? '—'}</KeyValue>
              <KeyValue label="Phone">{penalty.customerPhone ?? '—'}</KeyValue>
              <KeyValue label="Customer id">#{penalty.userId}</KeyValue>
            </KeyValueGrid>
          </Section>

          {penalty.status === 'SETTLED' || penalty.status === 'PENDING_SETTLEMENT' ? (
            <Section icon={<ShieldCheck aria-hidden="true" />} title="Collection">
              <KeyValueGrid>
                <KeyValue label="Booking">{penalty.settledBookingReference ?? '—'}</KeyValue>
                <KeyValue label="Reserved at">{formatDateTime(penalty.reservedAt)}</KeyValue>
                <KeyValue label="Settled at">{formatDateTime(penalty.settledAt)}</KeyValue>
                <KeyValue label="Paymob transaction">
                  <span className={styles.mono}>{penalty.settledPaymobTransactionId ?? '—'}</span>
                </KeyValue>
              </KeyValueGrid>
            </Section>
          ) : null}

          {penalty.status === 'WAIVED' ? (
            <Section icon={<HandCoins aria-hidden="true" />} title="Waiver">
              <KeyValueGrid>
                <KeyValue label="Waived by">{penalty.waivedByAdminName ?? '—'}</KeyValue>
                <KeyValue label="Waived at">{formatDateTime(penalty.waivedAt)}</KeyValue>
                <KeyValue label="Reason">{penalty.waiveReason ?? '—'}</KeyValue>
              </KeyValueGrid>
            </Section>
          ) : null}
        </div>
      ) : null}
    </Dialog>
  )
}

/** The sentence an operator can read to the customer. */
function explain(penalty: CustomerPenalty): string {
  const amount = formatMinorCurrency(penalty.amountMinor, penalty.currency)
  const price = formatMinorCurrency(penalty.bookingPriceMinor, penalty.currency)
  const window = describeWindow(penalty).toLowerCase()

  switch (penalty.status) {
    case 'SETTLED':
      return `${amount} was charged because booking ${
        penalty.sourceBookingReference ?? penalty.sourceTripRequestId
      } (${price}) was cancelled ${window}, and it has since been paid as part of ${
        penalty.settledBookingReference ?? 'a later booking'
      }.`
    case 'WAIVED':
      return `${amount} was charged for cancelling ${
        penalty.sourceBookingReference ?? penalty.sourceTripRequestId
      } ${window}, and was waived by ${penalty.waivedByAdminName ?? 'an administrator'}. The customer owes nothing.`
    case 'PENDING_SETTLEMENT':
      return `${amount} is being collected with booking ${
        penalty.settledBookingReference ?? 'the customer’s next booking'
      }. If that payment does not complete, the amount returns to the outstanding balance.`
    default:
      return `${amount} is outstanding because booking ${
        penalty.sourceBookingReference ?? penalty.sourceTripRequestId
      } (${price}) was cancelled ${window}. It will be added to the customer’s next booking, which must be paid by card.`
  }
}

function Section({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode
  title: string
  children: React.ReactNode
}) {
  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>
        {icon}
        {title}
      </h3>
      {children}
    </section>
  )
}
